import { z } from 'zod';

import {
  membershipStatusValues,
  type ChangeMembershipStatusRequest,
  type ChangeMembershipStatusResponse,
  type ChangeableMembershipStatus,
  type CompanyMembershipDto,
  type CompanyMembershipListItemDto,
  type CompanyMembershipRoleDto,
  type CompanyMembershipSummaryDto,
  type CompanyMembershipStatus,
  type GetMembershipRequest,
  type GetMembershipResponse,
  type InviteUserToCompanyRequest,
  type InviteUserToCompanyResponse,
  type ListMembershipsByCompanyRequest,
  type ListMembershipsByCompanyResponse,
} from './membership.dto.js';

const timestampSchema = z.iso.datetime();

export const CompanyMembershipSchema: z.ZodType<CompanyMembershipDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  userId: z.uuid(),
  status: z.enum(membershipStatusValues),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CompanyMembershipRoleSchema: z.ZodType<CompanyMembershipRoleDto> = z.object({
  roleId: z.uuid(),
  roleName: z.string().trim().min(1),
});

export const CompanyMembershipListItemSchema: z.ZodType<CompanyMembershipListItemDto> = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  userEmail: z.string().trim().pipe(z.email()),
  status: z.enum(membershipStatusValues),
  roles: z.array(CompanyMembershipRoleSchema),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const CompanyMembershipSummarySchema: z.ZodType<CompanyMembershipSummaryDto> = z.object({
  membershipId: z.uuid(),
  companyId: z.uuid(),
  companyName: z.string().trim().min(1),
  status: z.enum(membershipStatusValues),
});

export const InviteUserToCompanyRequestSchema: z.ZodType<InviteUserToCompanyRequest> = z.object({
  companyId: z.uuid(),
  email: z.string().trim().pipe(z.email()),
  roleIds: z.array(z.uuid()).default([]),
});

export const InviteUserToCompanyResponseSchema: z.ZodType<InviteUserToCompanyResponse> = z.object({
  membership: CompanyMembershipSchema,
});

export const ListMembershipsByCompanyRequestSchema: z.ZodType<ListMembershipsByCompanyRequest> = z.object({
  companyId: z.uuid(),
  status: z.enum(membershipStatusValues).optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const ListMembershipsByCompanyResponseSchema: z.ZodType<ListMembershipsByCompanyResponse> = z.object({
  items: z.array(CompanyMembershipListItemSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
  total: z.number().int().min(0),
});

export const ChangeMembershipStatusRequestSchema: z.ZodType<ChangeMembershipStatusRequest> = z.object({
  companyId: z.uuid(),
  membershipId: z.uuid(),
  status: z.enum(['active', 'disabled']),
});

export const ChangeMembershipStatusResponseSchema: z.ZodType<ChangeMembershipStatusResponse> = z.object({
  membership: CompanyMembershipSchema,
});

export const GetMembershipRequestSchema: z.ZodType<GetMembershipRequest> = z.object({
  companyId: z.uuid(),
  membershipId: z.uuid(),
});

export const GetMembershipResponseSchema: z.ZodType<GetMembershipResponse> = z.object({
  membership: CompanyMembershipSchema,
});

export const CompanyMembershipStatusSchema: z.ZodType<CompanyMembershipStatus> =
  z.enum(membershipStatusValues);

export const ChangeableMembershipStatusSchema: z.ZodType<ChangeableMembershipStatus> = z.enum([
  'active',
  'disabled',
]);
